import { getSelfPageToken } from '@quromedical/fhir-common';
import type { PatientResponse, PatientSearch } from '@quromedical/models';
import { omitby } from '@quromedical/utils';
import {
  type ColumnType,
  type SortConfig,
  useTableState,
} from 'components/table';
import { getDisplayDate } from 'core/display';
import { type DefinedGender, genderDisplay } from 'core/forms';
import { PaginatedTable } from 'core/table';
import { Section, Sidebar } from 'design-system/layout';
import { router, useLocalSearchParams } from 'expo-router';
import { type FetcherOptions, getField } from 'hooks/usePaginatedData';
import { PatientApi } from 'integration/aggregate';
import { usePaginationContext } from 'providers/PaginationContext';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { strings } from 'strings';
import { trpcClient } from 'trpc';

import { Header } from './Header';

const api = new PatientApi();

const columns: ColumnType<PatientResponse>[] = [
  {
    accessor: 'general',
    id: 'givenName',
    header: strings.GivenName,
    disableSortBy: false,
    Cell: ({ value }) => value.givenName || '',
  } as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'familyName',
    header: strings.FamilyName,
    disableSortBy: false,
    Cell: ({ value }) => value.familyName || '',
  } as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'gender',
    header: strings.Gender,
    disableSortBy: false,
    Cell: ({ value }: { value: PatientResponse['general'] }) =>
      genderDisplay[value.gender as DefinedGender] || '',
  } as unknown as ColumnType<PatientResponse, 'general'>,
  {
    accessor: 'general',
    id: 'birthDate',
    header: strings.DateOfBirth,
    disableSortBy: false,
    Cell: ({ value }: { value: PatientResponse['general'] }) =>
      getDisplayDate(value?.birthDate) || '',
  } as unknown as ColumnType<PatientResponse, 'general'>,
];

type Filter = Pick<PatientSearch, 'name'>;

const all = '__all__';

const shouldOmitOption = (value?: string): boolean => {
  if (!value?.length) {
    return true;
  }

  return value === all;
};

const sanitizeFilter = (data: Filter) => omitby(data, shouldOmitOption);

const sortConfig: SortConfig<PatientResponse> = {
  'general.givenName': {
    asc: 'given',
    desc: '-given',
  },
  'general.familyName': {
    asc: 'family',
    desc: '-family',
  },
  'general.gender': {
    asc: 'gender',
    desc: '-gender',
  },
  'general.birthDate': {
    asc: 'birthdate',
    desc: '-birthdate',
  },
};

export const PatientListScreen: React.FC<{}> = () => {
  const sidebarProps = Sidebar.useResponsiveSidebarProps();
  const [filter, setFilter] = useState<Filter>({});

  const { getPage, setPage, getToken, setToken } = usePaginationContext();
  const tableKey = 'patients';

  const { page: pageParam } = useLocalSearchParams<{ page?: string }>();

  const actualPage = useMemo(
    () => (pageParam ? parseInt(pageParam, 10) : getPage(tableKey)),
    [pageParam, getPage, tableKey],
  );

  const setSearch = useCallback(
    (name?: string) => setFilter({ ...filter, name }),
    [filter],
  );

  const tableState = useTableState<PatientResponse>({
    sort: sortConfig,
  });

  const cacheKey = [api.getPatientSWRKey('list'), filter, tableState.sort];

  const fetcher = useCallback(
    async (options?: FetcherOptions) => {
      const currentPage = options?.page ?? actualPage;
      const pageToken = options?.nextToken
        ? options.nextToken
        : getToken(tableKey, currentPage);

      const res = await trpcClient.patient.getAll.query({
        sort: tableState.sort,
        pageToken,
        ...sanitizeFilter(filter),
        ...options,
      });

      const selfPageToken = getSelfPageToken(res.link);
      setToken(tableKey, currentPage, selfPageToken);

      return res;
    },
    [filter, tableState, actualPage, getToken, setToken],
  );

  const onRowPress = useCallback(
    (row: PatientResponse) =>
      router.push({
        pathname: `/patient/${row.id}/profile`,
      }),
    [],
  );

  return (
    <Sidebar.Container>
      <Sidebar.Main {...sidebarProps.main}>
        <Section>
          <Header onSearchChange={setSearch} screen="patients" />
        </Section>
        <Section hasBottomMargin>
          <PaginatedTable
            initialPage={actualPage}
            cacheKey={cacheKey}
            fetcher={fetcher}
            getDataFromResponse={getField('patients')}
            onRowPress={onRowPress}
            onTableStateChanged={tableState.onChange}
            columns={columns}
            onPageChange={(page) => {
              setPage(tableKey, page);
            }}
          />
        </Section>
      </Sidebar.Main>
    </Sidebar.Container>
  );
};
